import { useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'
import { Check, Clock, Copy } from 'lucide-react-native'
import { colors } from '@chillout/core'

interface DevicePairingCodeCardProps {
  pairingCode: string
  formattedCode: string
  expiresAt: Date | null
  copyAccessibilityLabel: string
  showCopyIcon?: boolean
  onCopy: () => void
}

function buildExpiresLabel(
  expiresAt: Date,
  language: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const time = new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(expiresAt)
  const key = expiresAt.getDate() !== new Date().getDate()
    ? 'operations.addDevice.pairingCodeExpiresAtTomorrow'
    : 'operations.addDevice.pairingCodeExpiresAt'
  return t(key, { time })
}

export function DevicePairingCodeCard({
  pairingCode,
  formattedCode,
  expiresAt,
  copyAccessibilityLabel,
  showCopyIcon = true,
  onCopy,
}: DevicePairingCodeCardProps) {
  const { t, i18n } = useTranslation()
  const [copied, setCopied] = useState(false)

  function handleCopyPress() {
    onCopy()
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const expiresAtLabel = expiresAt ? buildExpiresLabel(expiresAt, i18n.language, t) : ''

  return (
    <View className="gap-4 rounded-lg border border-border bg-canvas-primary p-4 lg:flex-row lg:gap-5">
      <View className="items-center lg:items-start">
        <View className="bg-white p-2">
          <QRCode value={pairingCode} size={128} />
        </View>
      </View>
      <View className="flex-1 items-center justify-evenly gap-2 lg:items-start lg:gap-0">
        <View className="flex-row items-center gap-2">
          <Text className="text-center text-3xl font-bold tracking-widest text-accent lg:text-left">
            {formattedCode}
          </Text>
          {showCopyIcon && (
            <Pressable
              onPress={handleCopyPress}
              accessibilityLabel={copyAccessibilityLabel}
              accessibilityRole="button"
            >
              {copied ? (
                <Check size={18} color={colors.success} strokeWidth={2} />
              ) : (
                <Copy size={18} color={colors.text.secondary} strokeWidth={1.8} />
              )}
            </Pressable>
          )}
        </View>
        <View className="h-px w-full bg-border" />
        <View className="flex-row items-center gap-1.5">
          <Clock size={13} color={colors.text.secondary} strokeWidth={1.8} />
          <Text className="text-xs font-normal text-secondary">{expiresAtLabel}</Text>
        </View>
      </View>
    </View>
  )
}
